import { loadConfig } from "./core/config";
import { ApiClient } from "./core/api-client";
import { selectFeature, checkMicrophonePermission, checkVoiceUsable, FEATURE_LABELS } from "./core/feature-selector";
import { autoCollect } from "./core/visitor-info";
import { mountContainer, setActiveFeature, setFabPulse, getShadowRoot, setPanelContent, setFloatingIcons, showPanelBack, resetContainer } from "./ui/container";
import { renderCallbackPanel } from "./modules/callback/render";
import { renderVoicePanel } from "./modules/voice/render";
import { renderTextChatPanel } from "./modules/text-chat/render";
import type { InitOptions, WidgetConfig, Feature, SDKState } from "./types";

var state: SDKState = { config: null, activeFeature: null, panelOpen: false, micAvailable: null, initialized: false, error: null };
var _apiBase = resolveApiBase(), _visitorId = "", _heartbeatTimer: any = null, _apiClient: ApiClient | null = null, _debug = false;
var _initializing = false, _initPromise: Promise<void> | null = null;
function log(...args: unknown[]): void { console.log("[OneCallSDK]", ...args); }

/** 从 SDK 脚本的 src 自动推导 API 基地址 */
function resolveApiBase(): string {
  try {
    var scripts = document.getElementsByTagName("script");
    for (var i = scripts.length - 1; i >= 0; i--) {
      var src = scripts[i].src;
      if (src && (src.indexOf("/onecall-sdk") !== -1 || src.indexOf("/sdk/dist") !== -1)) {
        return new URL(src).origin + "/api";
      }
    }
  } catch {}
  return "/api";
}

export async function init(opts: InitOptions): Promise<void> {
  // 已初始化但 DOM 被外部移除（如 React Strict Mode），恢复挂载
  if (state.initialized) {
    if (!document.getElementById("onecall-widget-host")) {
      console.warn("[OneCallSDK] DOM 丢失，重新挂载");
      resetContainer();
      mountContainer(state.config!.theme, {
        onPanelOpen: function(feature?: Feature, ch?: string) {
          state.panelOpen = true;
          if (feature) { state.activeFeature = feature; setActiveFeature(feature); renderActiveFeature(feature); }
          else { renderHomeView(); }
        },
        onPanelClose: function() { state.panelOpen = false; },
      }, state.config!.buttonLogo, state.config!.aiAvatar);
      setActiveFeature(state.activeFeature);
    } else {
      console.warn("[OneCallSDK] 已初始化，跳过");
    }
    return;
  }
  if (_initializing) { console.warn("[OneCallSDK] 正在初始化，等待完成"); return _initPromise!; }
  _initializing = true;
  _initPromise = _doInit(opts);
  try { await _initPromise; } finally { _initializing = false; }
}

async function _doInit(opts: InitOptions): Promise<void> {
  _debug = opts.debug || (opts.config as any)?.debug || false;
  log("初始化...", opts);
  try {
    _apiBase = opts.apiBase || (typeof window !== "undefined" ? (window as any).__oc_apiBase__ : null) || "/api"; _apiClient = new ApiClient(_apiBase);
    if (typeof window !== "undefined") (window as any).__oc_apiBase__ = _apiBase;
    var vi = autoCollect(); _visitorId = vi.visitorId; log("访客信息:", _visitorId);
    state.config = await loadConfig(opts, _apiBase); log("配置:", state.config.features);
    try { await _apiClient.submitLead({ widgetId: opts.widgetId, visitorId: _visitorId, actionType: "page_view", pageUrl: vi.pageUrl, referrer: vi.referrer, utmParams: { source: vi.utm.source, medium: vi.utm.medium, campaign: vi.utm.campaign, term: vi.utm.term, content: vi.utm.content }, device: vi.deviceJson, browser: vi.browserJson }); } catch {}
    _heartbeatTimer = setInterval(function() { _apiClient!.sendHeartbeat(_visitorId, opts.widgetId).catch(function() {}); }, 30000);
    state.micAvailable = await checkMicrophonePermission().catch(function() { return false; });
    var result = await selectFeature(state.config.features, { voiceFallbackToCallback: state.config.voiceFallbackToCallback });
    state.activeFeature = result.feature; log("功能:", result);
    mountContainer(state.config.theme, {
      onPanelOpen: function(feature?: Feature, ch?: string) {
        state.panelOpen = true;
        if (feature) { state.activeFeature = feature; setActiveFeature(feature); renderActiveFeature(feature); }
        else { renderHomeView(); }
      },
      onPanelClose: function() { state.panelOpen = false; },
    }, state.config.buttonLogo, state.config.aiAvatar);
    var imIcons = (state.config.imChannels || []).filter(function(ch: any) { return ch.enabled; }).map(function(ch: any) { return { id: ch.id, name: ch.name, icon: ch.icon || "💬" }; });
    setFloatingIcons(state.config.features, imIcons);
    setActiveFeature(state.activeFeature);
    state.initialized = true; state.error = null; startWidgetGuard();
    log("初始化完成, 激活:", state.activeFeature ? FEATURE_LABELS[state.activeFeature] : "无");
  } catch (e) { state.error = e instanceof Error ? e.message : String(e); console.error("[OneCallSDK] 失败:", state.error); state.initialized = false; }
}

var _observer: any = null, _ot: any = null, _ore = false, _oreCount = 0, _oreCooldown = 0;
function startWidgetGuard(): void {
  if (_observer) return;
  _observer = new MutationObserver(function() {
    if (_ore) return;
    if (_ot) return;
    var now = Date.now();
    if (now < _oreCooldown) return;
    _ot = setTimeout(function() {
      _ot = null;
      if (!document.getElementById("onecall-widget-host")) {
        _oreCount++;
        if (_oreCount > 5) { _oreCooldown = now + 30000; _oreCount = 0; return; }
        _ore = true;
        try {
          log("恢复...");
          mountContainer(state.config!.theme, { onPanelOpen: function() { state.panelOpen = true; renderHomeView(); }, onPanelClose: function() { state.panelOpen = false; } }, state.config!.buttonLogo, state.config!.aiAvatar);
          setActiveFeature(state.activeFeature);
          _oreCount = 0;
        } finally { _ore = false; }
      }
    }, 500);
  });
  _observer.observe(document.body, { childList: true, subtree: true });
}
function stopWidgetGuard(): void { if (_ot) { clearTimeout(_ot); _ot = null; } if (_observer) { _observer.disconnect(); _observer = null; } }

function renderHomeView(): void {
  if (!state.config) return;
  showPanelBack(false);
  var cfg = state.config;
  var c = getComputedStyle(getShadowRoot()!.host).getPropertyValue("--oc-primary").trim() || "#533afd";
  var imChannels = (cfg.imChannels || []).filter(function(ch: any) { return ch.enabled; });
  var features = cfg.features || [];
  var hasVoice = features.indexOf("voice") !== -1;
  var hasTextChat = features.indexOf("text_chat") !== -1;
  var hasCallback = features.indexOf("callback") !== -1;
  var hasIM = features.indexOf("im") !== -1 && imChannels.length > 0;

  var h = '<div style="display:flex;flex-direction:column;height:100%"><div style="flex:1;overflow-y:auto;padding:16px">';

  if (hasVoice) h += '<button id="hm-voice" style="display:flex;align-items:center;width:100%;padding:13px 14px;border:1px solid #e8ecf1;border-radius:12px;background:#f8fafd;cursor:pointer;margin-bottom:8px;font-size:14px;color:var(--oc-text)" onmouseover="this.style.background=\'#eef2ff\';this.style.borderColor=\'' + c + '\'" onmouseout="this.style.background=\'#f8fafd\';this.style.borderColor=\'#e8ecf1\'"><span style="font-size:20px;width:32px;text-align:center">🎙</span><span style="flex:1;font-weight:500">语音对话</span><span style="color:#bbb;font-size:18px">›</span></button>';
  if (hasTextChat) h += '<button id="hm-text" style="display:flex;align-items:center;width:100%;padding:13px 14px;border:1px solid #e8ecf1;border-radius:12px;background:#f8fafd;cursor:pointer;margin-bottom:8px;font-size:14px;color:var(--oc-text)" onmouseover="this.style.background=\'#eef2ff\';this.style.borderColor=\'' + c + '\'" onmouseout="this.style.background=\'#f8fafd\';this.style.borderColor=\'#e8ecf1\'"><span style="font-size:20px;width:32px;text-align:center">💬</span><span style="flex:1;font-weight:500">文本对话</span><span style="color:#bbb;font-size:18px">›</span></button>';
  if (hasCallback) h += '<button id="hm-callback" style="display:flex;align-items:center;width:100%;padding:13px 14px;border:1px solid #e8ecf1;border-radius:12px;background:#f8fafd;cursor:pointer;margin-bottom:8px;font-size:14px;color:var(--oc-text)" onmouseover="this.style.background=\'#eef2ff\';this.style.borderColor=\'' + c + '\'" onmouseout="this.style.background=\'#f8fafd\';this.style.borderColor=\'#e8ecf1\'"><span style="font-size:20px;width:32px;text-align:center">📞</span><span style="flex:1;font-weight:500">回拨电话</span><span style="color:#bbb;font-size:18px">›</span></button>';

  if (hasIM) {
    var gu: Record<string, string> = { whatsapp: "https://wa.me/", messenger: "https://m.me/", instagram: "https://ig.me/m/", telegram: "https://t.me/", line: "https://line.me/R/ti/p/", wechat: "#", zalo: "https://zalo.me/", viber: "viber://chat?number=" };
    h += '<div><div style="font-size:11px;color:var(--oc-text-secondary);margin-bottom:8px">其他沟通方式</div><div style="background:#fff;border:1px solid var(--oc-border);border-radius:14px;overflow:hidden">';
    for (var i = 0; i < imChannels.length; i++) {
      var ch = imChannels[i], goUrl = gu[ch.id], phone = (ch.value || "").replace(/\+/g, ""), target = goUrl ? (goUrl === "#" ? "#" : goUrl + phone) : "#";
      h += '<a href="' + target + '" target="_blank" rel="noopener" style="display:flex;align-items:center;padding:13px 14px;text-decoration:none;color:var(--oc-text);border-bottom:1px solid #f5f5f5" onmouseover="this.style.background=\'#f9fafb\'" onmouseout="this.style.background=\'#fff\'"><div style="display:flex;align-items:center;gap:10px;flex:1"><img src="/imlogo/' + ch.id + '.svg" style="width:36px;height:36px;border-radius:6px" /><span style="font-size:14px">' + esc(ch.name) + '</span></div><span style="color:#bbb;font-size:18px">›</span></a>';
    }
    h += '</div></div>';
  }
  h += '</div></div>';
  setPanelContent(h);
  var root = getShadowRoot(); if (!root) return;
  root.getElementById("hm-voice")?.addEventListener("click", function() { renderFeature("voice"); });
  root.getElementById("hm-text")?.addEventListener("click", function() { renderFeature("text_chat"); });
  root.getElementById("hm-callback")?.addEventListener("click", function() { renderFeature("callback"); });
}

function renderFeature(feature: Feature): void {
  if (!state.config) return;
  showPanelBack(true);
  var loc = state.config.theme.locale || "zh-CN", wid = state.config.widgetId;
  setPanelContent("");
  if (feature === "text_chat") { renderTextChatPanel(loc, wid, state.config.receptionistId, state.config.receptionistName, renderHomeView, state.config.textChatWelcome, state.config.quickReplies); }
  else if (feature === "voice") { renderVoicePanel(loc, wid); }
  else if (feature === "callback") { renderCallbackPanel(loc, wid); }
}

function renderActiveFeature(overrideFeature?: Feature): void {
  if (!state.config) return;
  var f = overrideFeature || state.activeFeature; if (!f) return;
  showPanelBack(true);
  var loc = state.config.theme.locale || "zh-CN", wid = state.config.widgetId;
  setPanelContent("");
  if (f === "text_chat") { renderTextChatPanel(loc, wid, state.config.receptionistId, state.config.receptionistName, renderHomeView, state.config.textChatWelcome, state.config.quickReplies); }
  else if (f === "voice") { renderVoicePanel(loc, wid); }
  else if (f === "callback") { renderCallbackPanel(loc, wid); }
  else { renderHomeView(); }
}

function esc(s: string): string { if (!s) return ""; return s.split("&").join("&amp;").split("<").join("&lt;").split(">").join("&gt;").split("\"").join("&quot;"); }

export var getState = function(): Readonly<SDKState> { return state; };
export var openPanel = function(): void { if (!state.initialized) return; var r = getShadowRoot(), p = r?.getElementById("oc-panel"), f = r?.getElementById("oc-fab"); if (p && f) { p.classList.add("open"); f.classList.remove("pulse"); state.panelOpen = true; renderHomeView(); } };
export var closePanel = function(): void { if (!state.initialized) return; var r = getShadowRoot(), p = r?.getElementById("oc-panel"), f = r?.getElementById("oc-fab"); if (p && f) { p.classList.remove("open"); f.classList.add("pulse"); state.panelOpen = false; } };
export var createApiClient = function(): ApiClient { return new ApiClient(_apiBase); };
export var destroy = function(): void { if (_heartbeatTimer) { clearInterval(_heartbeatTimer); _heartbeatTimer = null; } stopWidgetGuard(); var h = document.getElementById("onecall-widget-host"); if (h) h.remove(); resetContainer(); state.initialized = false; state.config = null; };
export { renderVoicePanel, renderTextChatPanel, renderCallbackPanel };

var _existing = typeof window !== "undefined" ? ((window as any).SDK16x || (window as any).OneCallSDK) : null;
var _queue: any[] = _existing && Array.isArray(_existing.q) ? _existing.q.slice() : [];
if (typeof window !== "undefined") {
  console.log("[OneCallSDK] SDK 脚本已加载，版本 2.0");
  var api = { init: init, getState: getState, openPanel: openPanel, closePanel: closePanel, createApiClient: createApiClient, destroy: destroy };
  // 保留 q 属性兼容页面侧推送（防止页面反复 overwrite OneCallSDK）
  Object.defineProperty(api, "q", { value: { push: function(item: any) { var fn = (api as any)[item.method]; if (typeof fn === "function") try { fn.apply(null, item.args); } catch (e) { console.error("[OneCallSDK] q push 失败:", item.method, e); } } }, writable: false, configurable: false });
  (window as any).SDK16x = api;
  // 串行处理队列中的 init 调用，避免并发初始化
  (async function() {
    for (var idx = 0; idx < _queue.length; idx++) { var c = _queue[idx]; var fn = (api as any)[c.method]; if (typeof fn === "function") try { await fn.apply(null, c.args); } catch (e) { console.error("[OneCallSDK] 队列失败:", c.method, e); } }
  })();
}
